<template>
  <section>
    <nav class="columns">
      <div class="column">
        <b-field grouped group-multiline>
          <b-field>
            <b-datepicker
              :placeholder="
                $t('message.please_select') + $t('attributes.date_range')
              "
              v-model="selectedDates"
              range
              @input="onSelectedDates"
            >
            </b-datepicker>
          </b-field>

          <b-field>
            <b-input
              v-model="search"
              :placeholder="$t('attributes.search')"
              type="search"
              icon="magnify"
              @input="onFilter"
            ></b-input>
          </b-field>
          <b-field>
            <b-select
              v-model="selectedSource"
              name="source"
              @input="onFilterStatus"
            >
              <option value="">
                {{
                  $t('attributes.all') + $t('attributes.subscription_source')
                }}
              </option>
              <option value="網路">網路</option>
              <option value="公司系統">公司系統</option>
              <option value="上順官網-訂閱電子報">上順官網-訂閱電子報</option>
              <option value="其它">其它</option>
            </b-select>
          </b-field>
        </b-field>
      </div>
    </nav>

    <b-table
      :data="items"
      :loading="isLoading"
      :mobile-cards="false"
      hoverable
      narrowed
      bordered
      paginated
      pagination-position="both"
      backend-pagination
      backend-sorting
      :total="total"
      :current-page="parseInt(page)"
      :per-page="perPage"
      :default-sort-direction="defaultSortOrder"
      :default-sort="[sortField, sortOrder]"
      aria-next-label="Next page"
      aria-previous-label="Previous page"
      aria-page-label="Page"
      aria-current-label="Current page"
      @page-change="onPageChange"
      @sort="onSort"
    >
      <b-table-column
        field="email"
        :label="$t('attributes.email')"
        width="200"
        sortable
        v-slot="props"
      >
        {{ props.row.email }}
      </b-table-column>

      <b-table-column
        field="user_name"
        :label="$t('attributes.subscriber')"
        sortable
        v-slot="props"
      >
        {{ props.row.user_name }}
      </b-table-column>

      <b-table-column
        field="source"
        :label="$t('attributes.subscription_source')"
        width="200"
        sortable
        v-slot="props"
      >
        {{ props.row.source }}
      </b-table-column>

      <b-table-column
        field="create_time"
        :label="$t('attributes.subscription_time')"
        width="180"
        sortable
        centered
        v-slot="props"
      >
        {{ formatDateTime(props.row.create_time) }}
      </b-table-column>

      <template #empty>
        <section class="section">
          <div
            v-if="!loadingItems"
            class="content has-text-grey has-text-centered"
          >
            <p>
              <b-icon icon="emoticon-sad" size="is-large" />
            </p>
          </div>
        </section>
      </template>
    </b-table>

    <b-loading :active.sync="isLoading" :can-cancel="false"></b-loading>
  </section>
</template>

<script>
import debounce from 'lodash/debounce'
import { SUBSCRIPTIONS_API } from '@/utils/apis'
import notifyService from '@/services/notifyService'

export default {
  name: 'SubscriptionList',

  props: {
    query: {
      type: Object,
      required: true
    }
  },
  data() {
    const { startDate, endDate } = this.calculateDateRange(this.query)

    return {
      sending: false,
      loadingItems: false,
      total: 0,
      items: [],
      selectedItem: null,

      selectedSource: this.query.source || '',
      search: this.query.search || '',
      sortField: this.query.sort || 'create_time',
      sortOrder: this.query.order || 'desc',
      defaultSortOrder: 'desc',
      page: this.query.page || 1,
      perPage: this.query.page_size || 10,

      selectedDates: [startDate, endDate],
      startDate,
      endDate
    }
  },
  watch: {
    query(query) {
      this.getQuery(query)
      this.loadItems()
    }
  },
  computed: {
    isLoading() {
      return this.loadingItems || this.sending
    }
  },
  mounted() {
    this.loadItems()
  },
  methods: {
    async loadItems() {
      try {
        const params = {
          source: this.selectedSource,
          search: this.search,
          start_date: this.startDate,
          end_date: this.endDate,
          sort_by: this.sortField,
          by_descending: this.sortOrder !== 'asc',
          page_size: this.perPage,
          page_number: this.page
        }

        this.loadingItems = true
        const res = await this.axios.get(SUBSCRIPTIONS_API, { params })
        const data = res.data.data

        this.items = data.items
        this.total = data.item_count
      } catch (e) {
        this.items = []
        this.total = 0

        notifyService.fetch_error('edm', e)
      } finally {
        this.loadingItems = false
      }
    },
    getQuery(query) {
      this.selectedSource = query.source || ''
      this.search = query.search || ''
      this.sortField = query.sort || 'create_time'
      this.sortOrder = query.order || 'desc'
      this.page = query.page || 1
      this.perPage = query.page_size || 10

      const { startDate, endDate } = this.calculateDateRange(query)
      this.startDate = startDate
      this.endDate = endDate
      this.selectedDates = [startDate, endDate]
    },
    calculateDateRange(query) {
      let startDate = null
      let endDate = null

      if (query.start && query.end) {
        startDate = this.$dayjs.unix(query.start).toDate()
        endDate = this.$dayjs.unix(query.end).toDate()
      } else {
        const today = this.$dayjs()
        startDate = today.subtract(5, 'year').toDate()
        endDate = today.toDate()
      }

      return { startDate, endDate }
    },
    updateQuery() {
      const query = {
        source: this.selectedSource,
        search: this.search,
        start: this.startDate && this.$dayjs(this.startDate).unix(),
        end: this.endDate && this.$dayjs(this.endDate).unix(),
        sort: this.sortField,
        order: this.sortOrder,
        page_size: this.perPage.toString(),
        page: this.page.toString()
      }

      Object.keys(query).forEach(key => !query[key] && delete query[key])

      this.$router
        .push({
          name: this.$route.name,
          query: query
        })
        .catch(() => {})
    },
    onFilterStatus() {
      this.page = 1
      this.updateQuery()
    },
    onSelectedDates() {
      this.page = 1
      this.startDate = this.selectedDates[0]
      this.endDate = this.selectedDates[1]

      this.updateQuery()
    },
    onClearDates() {
      this.page = 1
      this.selectedDates = null
      this.startDate = null
      this.endDate = null

      this.updateQuery()
    },
    onFilter: debounce(function() {
      this.page = 1
      this.updateQuery()
    }, 500),
    onPageChange(page) {
      this.page = page
      this.updateQuery()
    },
    onSort(field, order) {
      this.sortField = field
      this.sortOrder = order
      this.updateQuery()
    }
  }
}
</script>

<style lang="scss" scoped>
.b-table {
  margin-top: 0.75rem;

  td {
    vertical-align: middle;
  }
}
</style>
